import { useTranslation } from "react-i18next";
import { FormCard, FormGrid } from "../../FormCard";
import { DetailRow } from "../../Rows";
import TariffElementsOverview from "../TariffElementsOverview";
import TariffAssignmentOverview from "../TariffAssignmentOverview";
import { TAX_INCLUDED_LOCKED } from "../tariffOptions";
import { ENERGY_MODE, toElements } from "./tariffWizardState";

const PreviewStep = ({
    state,
    connectors = [],
    customers = [],
    countryOptions = [],
    organizationName = "",
}) => {
    const { t, i18n } = useTranslation();
    const basics = state.basics;
    const currency = basics.currency || "";
    const countryLabel =
        countryOptions.find((country) => country.value === basics.countryCode)?.label ||
        basics.countryCode ||
        "";
    const altTexts = (basics.altTexts || []).filter((alt) => alt.text || alt.language);
    const assignedConnectors = connectors.filter((connector) =>
        (state.assignment.chargingConnectorPublicIds || []).includes(connector.chargingConnectorPublicId)
    );
    const assignedCustomers = customers.filter((customer) =>
        (state.assignment.customerPublicIds || []).includes(customer.customerPublicId)
    );

    return (
        <div className="flex flex-col gap-6">
            <FormCard title={t("common.details")}>
                <FormGrid>
                    {organizationName ? (
                        <DetailRow label={t("entity.organization")} value={organizationName} />
                    ) : null}
                    <DetailRow
                        label={t("form.isPublic")}
                        value={basics.isPublic ? t("common.yes") : t("common.no")}
                    />
                    <DetailRow label={t("common.currency")} value={basics.currency} />
                    <DetailRow
                        label={t("form.tariffType")}
                        value={t(`tariff.type.${basics.tariffType}`, { defaultValue: basics.tariffType })}
                    />
                    <DetailRow label={t("common.country")} value={countryLabel} />
                    <DetailRow
                        label={t("form.taxIncluded")}
                        tooltip={t("tariff.tooltip.taxIncludedLocked")}
                        value={t(`tariff.taxIncluded.${TAX_INCLUDED_LOCKED}`, { defaultValue: TAX_INCLUDED_LOCKED })}
                    />
                    <DetailRow label={t("form.startDateTime")} value={formatDateTime(basics.startDateTimeUtc, i18n.language)} />
                    <DetailRow label={t("form.endDateTime")} value={formatDateTime(basics.endDateTimeUtc, i18n.language)} />
                    <DetailRow
                        label={t("form.preauthorizeAmount")}
                        value={formatPrice(basics.preauthorizeAmount, currency)}
                    />
                </FormGrid>
                {altTexts.length > 0 && (
                    <div className="mt-10">
                        <h3 className="text-lg font-semibold mb-3">{t("form.altTexts")}</h3>
                        <FormGrid>
                            {altTexts.map((alt, index) => (
                                <DetailRow
                                    key={`${alt.language}-${index}`}
                                    label={alt.language || t("form.altText")}
                                    value={alt.text}
                                />
                            ))}
                        </FormGrid>
                    </div>
                )}
            </FormCard>

            <TariffElementsOverview
                elements={toElements(state)}
                currency={currency}
                spotEnergy={state.energyMode === ENERGY_MODE.SPOT ? state.energy : null}
            />

            <TariffAssignmentOverview
                isPublic={Boolean(basics.isPublic)}
                connectors={assignedConnectors}
                customers={assignedCustomers}
            />
        </div>
    );
};

function formatDateTime(value, language) {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const locale = String(language || "en").startsWith("sv") ? "sv-SE" : "en-GB";
    return date.toLocaleString(locale);
}

function formatPrice(value, currency) {
    if (value === "" || value == null) {
        return "";
    }
    return currency ? `${value} ${currency}` : String(value);
}

export default PreviewStep;
